import { TenantConfigViewItem, ViewDefnState } from 'src/dao/tenantConfigClient';
import { ColDef, ValueParserParams, ValueGetterParams, ValueFormatterParams } from '@ag-grid-community/core';
import { SubheaderDropdownProps } from '../Subheader/SubheaderDropdown';
import { SalesAdjustmentConfig } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Editors/SalesAdjustmentEditor';
import { ScrollTo } from 'src/common-ui/components/DataGrid/DataGrid';
import { CollapsibleCompanion, SortOption } from 'src/common-ui/components/CompanionListView/CompanionListView';
import { ParamedCalc } from 'src/utils/LibraryUtils/MathUtils';
import { StyleEditConfigColumn } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.types';
import { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AppThunkDispatch } from 'src/store';
import { ConfigurableGridGroupBySelection } from './ConfigurableGrid.slice';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { ClientDataApi, DataApi, WorklistConfigButtons } from 'configuration-schemas/confdefnView';
import { MassEditConfig } from '../MassEdit/MassEditv2';
import { ConfigurableGridOwnProps } from 'configuration-schemas/ownProps';
import { SelectorSubheaderDropdownProps } from './ConfigurableGrid.selectors';
import { ValidValuesCache } from 'src/services/validValuesCache';
import { CoarseEditPayload } from 'src/dao/pivotClient';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import {
  ConfigurableGridActions,
  ConfigurableGridButton,
  ConfigurablePostAction,
  GroupViewItem,
  MassEditCoordinateMap,
} from 'configuration-schemas/viewdefns/general';
import { Scope } from 'src/types/Scope';
import { ViewDataState } from 'src/types/Domain';
import { Option } from '../Configure/ConfigureModal';
import { ParseResult } from '../Configure/Configure';
import { StyleDetailsPopoverProps } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { FabProps } from 'src/components/higherOrder/withFab';
import { WithRouterProps } from 'src/components/higherOrder/withRouter';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { GroupByPayload, SubheaderSlice } from '../Subheader/Subheader.slice';
import { TopMembers } from 'src/services/Scope.client';
import { WithBreadCrumbProps } from '../higherOrder/withBreadcrumb';
import { MasterDetailConfig } from 'src/utils/Component/AgGrid/AgConfigParse';

import { BreadcrumbRestore } from 'src/state/breadcrumbs/Bread.slice';
import { ConfigurableGridViewDefn, refreshOn } from 'configuration-schemas/viewdefns/viewdefn';
import { BarFillRendererParams } from 'src/common-ui/components/BarFillRenderer/BarFillRenderer';

export interface GridField {
  headerName?: string;
  colId?: string;
  pinned?: boolean;
  cellClass?: string | string[];
  renderer?: string;
  hide?: boolean;
  valueParser?: ((params: ValueParserParams) => any) | string;
  valueGetter?: ((params: ValueGetterParams) => any) | string;
  valueFormatter?: (params: ValueFormatterParams) => string;
  footerValueGetter?: ((params: ValueGetterParams) => any) | string;
  editable?: boolean;
  field?: string;
  aggFunc?: string;
  dataIndex?: string;
}

export enum AggOptions {
  Sum = 'sum',
  Min = 'min',
  Max = 'max',
  Count = 'count',
  Avg = 'avg',
  First = 'first',
  Last = 'last',
  TotalName = 'totalName',
  Eval = 'eval',
  SortByFirst = 'sortByFirst',
}

export type AggregatorParams = {
  dataIndex: string;
  // to add more later
};
export interface ColumnConfig extends Omit<TenantConfigViewItem, 'pinned'> {
  pinned?: boolean;
  editable?: boolean;
  calculation?: string;
  inputType?: string;
  dataApi?: DataApi;
  configApi?: ClientDataApi;
  renderer?: string;
  hidden?: boolean;
  width?: number;
  children?: ColumnConfig[];
  headerName?: string;
  aggregator?: AggOptions;
  aggregatorParams?: AggregatorParams;
  enableValue?: boolean;
}

export type GroupColumnConfig = {
  width?: number;
  classes?: string[];
};

export interface TrueColDef extends ColDef {
  renderer?: string;
  footerValueGetter?: () => string; // subject to extensive change. Lazy right now
}

export interface ConfigurableGridColumnDef extends StyleEditConfigColumn {
  useMassEditUpdate: boolean; // uses new endpoint for updates (currently single datapoint update only)
  useColumnHeaderMassEditUpdate?: boolean;
  postArrayAsString?: boolean;
  width?: number;
  dataApiLifecycle?: ClientDataApi;
  dataApiStore?: ClientDataApi;
  lifecycleConfig?: ClientDataApi;
  storeConfig?: ClientDataApi;
  dependentsApi?: ClientDataApi;
  cascadeGroupSelection?: boolean;
  dependents?: string[];
  tooltipDataIndex?: string;
  masterDetail?: MasterDetailConfig;
  headitorParams?: {
    editType: string;
    tooltip?: string;
  };
  rendererParams?: TemplateRendererParams;
  aggregatorParams?: AggregatorParams;
  aggRendererParams?: ConfigurableGridConfigItem['aggRendererParams'];
}

export interface ConfigurableGridDispatchProps extends Pick<CollapsibleCompanion, 'onToggleCompanion'> {
  dispatch: AppThunkDispatch;
  updateGroupBy: ActionCreatorWithPayload<GroupByPayload | number | null>;
  updateTopMemberSelection: ActionCreatorWithPayload<string>;
  onUpdateConfig: (config: any) => void;
  updateAssortmentPlan: () => void;
  onRefreshConfigurableGridData: () => void;
  onRefreshTopAttributes: () => void;
  submitPayload: (payload: CoarseEditPayload) => Promise<void>;
  updateConfigureSelections?(selections: Option[]): void;
  addSelectedItemsToCart(): void;
  toggleSelections(selections: BasicPivotItem | BasicPivotItem[]): void;
  syncGridData(redecoratedData: BasicPivotItem[]): void;
  createMfpScope(topMembers: TopMembers, workflow: string): Promise<unknown>;
  setPreviousView?(url: string): void;
  updateCompanionSortField(field: string): void;
  updateCompanionSortDirection(dir: SortByDirection): void;
  doesRowPassFilter(row: BasicPivotItem): boolean;
  /** Notify external views (for example `HistoryStylePane`) that grid rows changed so they can redecorate */
  requestExternalDataRefresh(payload: PendingRedecorateActionPayload): void;
}

/** State for when a component from outside `ConfigurableGrid`
 * needs to tell the grid to redecorate, such as Edits from Style Pane
 */
export interface PendingRedecorateState {
  /** The source of the redecorate request, the component that fired the edit */
  sourceComponent: string;
  styleIds: string[];
  stylecolorIds: string[];
  dataIndices: string[];
  editTime: number;
}
/** `editTime` is an internal concept for `ConfigurableGrid`
 * This omit type is for epics/actions/etc.
 */
export interface PendingRedecorateActionPayload extends Omit<PendingRedecorateState, 'editTime'> {}

export interface ConfigurableGridValueProps
  extends Pick<ConfigurableGridOwnProps, 'topMembers'>,
    Pick<CollapsibleCompanion, 'isCompanionCollapsed'>,
    Pick<SubheaderSlice, 'companionSortField' | 'companionSortDirection'> {
  /** @param idProp this is used for linking components (f/e companion list and grid) */
  idProp: string;
  /** @param  leafIdProp is used for grid level row identification (for example, row selection) */
  leafIdProp: string;
  /** external redecorate trigger (for example from HistoryStylePane); consumed by EditableGrid */
  pendingRedecorate?: PendingRedecorateState;
  viewDefnState: ViewDefnState;
  dataLoaded: boolean;
  favoritesList: FavoriteResponseItem[];
  groupBySelection?: ConfigurableGridGroupBySelection;
  configureOptions?: ParseResult;
  configureSelections?: Option[];
  columnDefs: ConfigurableGridColumnDef[];
  clientActionHandlers?: ClientActionHandler;
  dependentCalcs: {
    [key: string]: ParamedCalc;
  };
  massEditConfig?: MassEditConfig;
  updateCoordinateMap?: MassEditCoordinateMap;
  onlySendCoordinateMap?: boolean;
  // TODO: these need to point to viewdefn validator type
  // and fix types that have been ignored trickling down
  configuratorViewDefn?: ConfigurableGridViewDefn;
  unmodifiedViewDefn?: ConfigurableGridViewDefn;
  gridRowHeight?: number;
  groupRowHeight?: number;
  defaultCompanionSortField?: string;
  companionSortOptions: SortOption[];
  groupByDropdownProps?: SelectorSubheaderDropdownProps;
  floorsetDropdownProps?: SelectorSubheaderDropdownProps;
  data: BasicPivotItem[];
  topAttributesData: BasicPivotItem | undefined;
  topAttributesRefresh: refreshOn;
  topAttributesDataState: ViewDataState;
  salesAdjustmentConfig?: SalesAdjustmentConfig;
  flowStatus: number[];
  search: string;
  showPublish?: boolean;
  hideUnpublish?: boolean;
  unpublishText?: string;
  adornments: AdornmentType[];
  publishText?: string;
  publishAttribute?: string;
  configurablePostAction: ConfigurablePostAction;
  scopeSelection: Scope;
  viewDataState: ViewDataState;
  showUndo: boolean;
  isWorklistActive: boolean;
  fab: FabProps;
  assortmentCartLink?: string;
  cartCount?: number;
  allowWorklistFunctionality?: boolean;
  enableCheckboxSelection?: boolean;
  actions?: ConfigurableGridActions;
  showLookBackPeriod: boolean;
  lookBackPeriod?: string;
  worklistConfigButtons?: WorklistConfigButtons;
  sideNavCollapsed: boolean;
  groupColumn?: GroupColumnConfig;
  activeBreadcrumbRestore: BreadcrumbRestore | null;
  selectedLevel?: GroupViewItem;
}

export interface ConfigurableGridProps
  extends ConfigurableGridOwnProps,
    ConfigurableGridDispatchProps,
    ConfigurableGridValueProps,
    StyleDetailsPopoverProps,
    WithRouterProps,
    WithBreadCrumbProps {}

export interface ConfigurableGridState {
  /** used to signal to MassEdit that the grid is ready */
  gridRdyCnt: number;
  selectedId?: string;
  gridScrollTo?: ScrollTo;
  companionScrollTo?: ScrollTo;
  activeStyleColor: string;
  subheaderDropdownConfig?: SubheaderDropdownProps[];
  favoritesList?: FavoriteResponseItem[];
  groupByIndexTemp?: number;
  isConfirmationModalOpen: boolean;
  isConfirmationModalLoading: boolean;
  actionType: 'publish' | 'unpublish' | 'link' | undefined;
  validValuesCache: ValidValuesCache;
  configureIsOpen?: boolean;
  currentConfigureSelections?: Option[];
  actionPopupActive?: boolean;
  actionPopupBtnConf?: ConfigurableGridButton;
  isMfpScopeLoading: boolean;
  isLinkModalOpen: boolean;
  /**
   * Tracks whether a 'refreshOn: all' top-attributes refresh is in-flight
   * so the macro section can show its skeleton instead of unmounting
   */
  isTopAttributesRefreshing: boolean;
  /** the collapsed state per column group */
  columnGroupState?: { groupId: string; open: boolean }[];
}

/** This is a type like `TenantConfigViewItem` that contains a lot of properties of other types used for various uses
 * and contains properties used by several different types that don't all need everything from every type.
 */
export interface TemplateRendererParams
  extends Pick<BarFillRendererParams, 'color' | 'colorDataIndex' | 'innerRenderer'> {
  mask: string;
  maskParams: Record<string, string>;
  aggregateIgnoreDisabled?: boolean;
  classByCalc?: Record<string, string>;
  innerRenderer?: string;
  confirmationModal?: {
    description: string;
  };
  /** calculation to determine if link is navigable */
  navigableByCalc?: string | ParamedCalc;
}
export interface ConfigurableGridConfigItem extends StyleEditConfigColumn {
  rendererParams?: TemplateRendererParams;
  width?: number;
  dataApiLifecycle?: ClientDataApi;
  dataApiStore?: ClientDataApi;
  lifecycleConfig?: ClientDataApi;
  storeConfig?: ClientDataApi;
  dependentsApi?: ClientDataApi;
  postArrayAsString?: boolean;
  cascadeGroupSelection?: boolean;
  tabIndex?: number;
  dependents?: string[];
  wrapText?: boolean;
  wrapHeaderText?: boolean;
  useColumnHeaderMassEditUpdate?: boolean;
  classes?: string[];
  refData?: ColDef['refData'];
  tooltipDataIndex?: string;
  aggRendererParams?: {
    comparisonDataIndex: string;
  };
  headitorParams?: {
    editType: string;
    tooltip?: string;
  };
  masterDetail?: MasterDetailConfig;
}

export interface ClientActionHandler {
  [clientHandler: string]: string[]; // value is array of dataIndices that require clientHandler logic
}

export interface ClientActionHandlersConfig {
  [action: string]: ClientActionHandler;
}

/**
 * State required to be implemented by any popup editors that are enabled for aggregate cell edits
 * */
export interface ConfigurableGridAggCellEditorState {
  valueChosen: boolean;
}

export interface MassColumnUpdateParams {
  value: string;
  dataIndex: string;
  nodes: BasicPivotItem[];
}

export enum AsyncCellState {
  Idle = 'Idle',
  Processing = 'Processing',
  Redecorating = 'Redecorating',
}
